/**
 * The top navigation bar shown on every page.
 *
 * Left: brand mark + wordmark, linking to `/` (which routes to
 * `/dashboard` via RequireAuth for authenticated users or `/sign-in`
 * otherwise). Right: if signed in, shows the user's email and an
 * Admin link for super admins; otherwise nothing.
 *
 * The sticky, blurred, cream background is the same treatment the
 * Phase 0 landing page used — extracted here so every page shares
 * one implementation.
 */

import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/features/auth/useAuth";
import { getEffectiveTheme, toggleTheme, type Theme } from "@/lib/theme";

export function TopBar() {
  const { user, isSuperAdmin, hasFamily } = useAuth();
  const location = useLocation();
  const [theme, setTheme] = useState<Theme>(getEffectiveTheme);

  const handleToggleTheme = useCallback(() => {
    const next = toggleTheme();
    setTheme(next);
  }, []);

  const isOnAdmin = location.pathname.startsWith("/admin");
  const isOnEvents = location.pathname === "/events";
  const isOnFamily = location.pathname.startsWith("/family");
  const isOnChildren = location.pathname.startsWith("/children");
  const isOnDashboard = location.pathname === "/dashboard";

  return (
    <header className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-11 py-5 border-b border-edge-soft backdrop-blur-[10px] bg-[rgba(255,245,226,0.75)] dark:bg-[rgba(26,18,16,0.85)]">
      <Link
        to="/"
        className="flex items-center gap-[14px] no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded shrink-0"
      >
        <BrandMark />
        <Wordmark className="hidden sm:block" />
      </Link>

      {user && (
        <nav className="flex items-center gap-3 md:gap-6">
          {hasFamily && (
            <Link
              to="/dashboard"
              className={`font-sans text-[13px] md:text-[14px] font-medium no-underline pb-1 transition-colors ${
                isOnDashboard
                  ? "text-ink border-b-[1.5px] border-rwar-red"
                  : "text-ink-muted hover:text-ink-soft"
              }`}
            >
              Dashboard
            </Link>
          )}
          {hasFamily && (
            <Link
              to="/children"
              className={`font-sans text-[13px] md:text-[14px] font-medium no-underline pb-1 transition-colors ${
                isOnChildren
                  ? "text-ink border-b-[1.5px] border-rwar-red"
                  : "text-ink-muted hover:text-ink-soft"
              }`}
            >
              Children
            </Link>
          )}
          {hasFamily && (
            <Link
              to="/events"
              className={`font-sans text-[13px] md:text-[14px] font-medium no-underline pb-1 transition-colors ${
                isOnEvents
                  ? "text-ink border-b-[1.5px] border-rwar-red"
                  : "text-ink-muted hover:text-ink-soft"
              }`}
            >
              Journal
            </Link>
          )}
          {hasFamily && (
            <Link
              to="/family"
              className={`font-sans text-[13px] md:text-[14px] font-medium no-underline pb-1 transition-colors ${
                isOnFamily
                  ? "text-ink border-b-[1.5px] border-rwar-red"
                  : "text-ink-muted hover:text-ink-soft"
              }`}
            >
              Family
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              to="/admin"
              className={`font-sans text-[13px] md:text-[14px] font-medium no-underline pb-1 transition-colors ${
                isOnAdmin
                  ? "text-ink border-b-[1.5px] border-rwar-red"
                  : "text-ink-muted hover:text-ink-soft"
              }`}
            >
              Admin
            </Link>
          )}
          <span
            className="hidden md:inline font-sans text-[13px] text-ink-soft"
            aria-label={`Signed in as ${user.email}`}
          >
            {user.email}
          </span>
          <button
            type="button"
            onClick={handleToggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="w-8 h-8 flex items-center justify-center rounded-full text-ink-muted hover:text-ink-soft"
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
        </nav>
      )}
    </header>
  );
}
